<template lang="pug">
  .link-wrap(:class="[isEventPage && 'black', isTheArnoldPage && 'the-arnold']")
    .links.main-container
      .link.red-link.has-dropdown.has-icon.hover-dropdown(
        onmouseenter="document.activeElement.blur()"
        tabindex="-1"
      )
        button.shop-btn(role="button" tabindex="0") {{ __('Shop', 'categories') }}
        Icon(name="arrow-down" fill="white" tabindex="-1")
        shop-menu.dropdown.shop-menu(data-track="megamenu")
      .top-nav-links.hideable-links(:class="isEventPage && 'hide-mobile'" data-track="top-nav-links")
        template(v-for='link in topNavLinks' :key="link.url")
          rogue-link.link(
            :to='link.url'
            :categoryId="link.id"
            :class="[link.url === $route.path && 'active', link.image && 'has-icon']"
            :style='{ order: link.priority }'
            :aria-label="`Link to ${link.name}`"
          )
            StaticImage(
              v-if="link.image"
              :src="link.image.src"
              background="transparent"
              :width="link.image.width"
              :height="link.image.height"
              alt=""
              contain
              ssr)
            span(v-html="__(link.name, 'categories')")
          rogue-link.link.deals-link-mobile(
            v-if="link.name === 'Apparel'"
            to="/deals"
            :class="$route.path === '/deals' && 'active'"
            aria-label="Link to Deals"
          )
            span {{ __('Deals', 'categories') }}
      .mobile-event-links.hideable-links(v-if="isEventPage && isMounted" :style="{ minWidth: dynamicMinWidth }" data-track="event-nav-links" )
        rogue-link.event-logo-link.link(:to="eventBaseUrl" v-if="isEventPage && isMounted && eventLogo")
          StaticImage.event-logo(
            :src="eventLogo.mobile.src"
            :alt="__('Event home')"
            :width="eventLogo.mobile.width"
            :height="eventLogo.mobile.height"
            eager
          )
        rogue-link.link(
          v-for='link in eventNavLinks'
          :key="link.url"
          :to='link.url'
          :aria-label="`Link to ${link.name}`"
        )
          span {{ __(link.name, eventNamespace) }}
      rogue-link.link.red-link(to="/deals" data-track="top-nav-links" aria-label="Link to Deals")
        span {{ __('Deals', 'categories') }}
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onServerPrefetch, ref } from 'vue'
import { useRoute } from 'vue-router'
import { eventNamespace } from 'sisu/js/constants'
import { useRogue } from '@/js/services/Rogue'
import { useUIStore } from '@/js/stores/uiStore'

const neverHydrate = () => { }

const ShopMenu = defineAsyncComponent({
  loader  : () => import(/* webpackChunkName: "ssr-header-static"*/ '@/js/components/header/shop-menu.vue'),
  hydrate : neverHydrate,
})

const route = useRoute()
const rogue = useRogue()
const uiStore = useUIStore()

type link = { name: string, id: number, url: string }

const isMounted = ref<boolean>(false)

const isEventPage = computed(() => {
  const eventPaths = [
    '/invitational',
    '/arnold-strongman-classic',
    '/events/invitational',
    '/events/arnold-strongman-classic',
  ]

  return eventPaths.some(p => route.fullPath.startsWith(p))
})

const isTheArnoldPage = computed<boolean>(() => isEventPage.value && route.fullPath.includes('arnold'))

const topNavLinks = computed<link[]>(() => uiStore.topNavLinks)
const eventNavLinks = computed<link[]>(() => uiStore.eventNavLinks)
const eventBaseUrl = computed<string | null | undefined>(() => uiStore.eventBaseUrl)
const eventLogo = computed<string | null | undefined>(() => uiStore.eventLogo)
const dynamicMinWidth = computed<string>(() => {
  const linkCount = eventNavLinks.value.length + 1
  const avgLinkWidth = 118

  return `${Math.ceil(linkCount / 2) * avgLinkWidth}px`
})

async function load() {
  const siteNavLinks = await import(`@/assets/top-nav/${rogue.site.code.toLowerCase()}.ts`)
  uiStore.topNavLinks = siteNavLinks.topNavLinks
}

onMounted(async () => {
  if (!topNavLinks.value.length) {
    await load()
  }
  isMounted.value = true
})

onServerPrefetch(async () => await load())

</script>
<style lang="scss" scoped>

.dropdown {
  background-color: $color-white;
  display: none;
  z-index: 100;
}

.link {
  text-decoration: none;
}

.link > span {
  display: block;
  padding: 0 6px;
  line-height: 100%;
  font-family: $font-condensed;
  white-space: nowrap;
}

.links {
  height: 100%;
  display: flex;
  align-items: center;

  &::-webkit-scrollbar {
    display: none;
  }
}

.hideable-links {
  display: flex;
}

.link.has-icon {
  display: flex;
}

/**
Mobile Links
 */
@include not-desktop() {
  .link-wrap {
    padding-bottom: $space-xxxs;
    padding-top: $space-xxxs;
    background-color: $color-gray-x-light;
    height: 92px;

    &.the-arnold {
      height: 50px;
    }

    &.black {
      background-color: $color-black;
    }
  }

  .link.has-dropdown.has-icon {
    width: 0;
    overflow: hidden;
  }

  .link.red-link {
    width: 0;
    margin: 0;
    overflow: hidden;
  }

  a.link.red-link {
    display: none;
  }

  .links {
    overflow-x: auto;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    margin: 0;
    padding: 0 6px;
  }

  .link {
    border-radius: $radius-default;
    background-color: $color-gray-light;
    margin-right: $space-xxs;
  }

  .link > span {
    font-size: 16px;
    line-height: 32px;
    color: $color-black-light;
  }

  .link > .rg-img {
    width: 16px;
    margin-left: $space-xxs;
  }

  .dropdown {
    z-index: 101;
    flex-direction: column;
    position: fixed;
    background-color: white;
    width: 350px;
    max-width: 100%;
    left: 0;
    transform: translateX(-350px);
    pointer-events: none;
    top: 0;
    bottom: 0;
    transition: transform 300ms;

    .show-mobilemenu & {
      transform: translateX(0);
      pointer-events: all;
    }
  }

  .hideable-links {
    flex-wrap: wrap;

    min-width: 578px;

    &.top-nav-links.hide-mobile {
      display: none;
    }
  }

  .the-arnold {
    .hideable-links {
      min-width: fit-content;
      flex-wrap: nowrap;
    }
  }

  .link {
    margin-top: 5px;
    margin-bottom: 5px;
  }

  .event-logo-link.link {
    display: flex;
    align-items: center;
    border-radius: $radius-default;
    margin-right: $space-xxs;
    background-color: $color-gray-light;
    height: 32px;
    padding-left: 5px;
    padding-right: 5px;
    width: 75px;

    .event-logo {
      width: 65px;
      max-height: 30px;
      margin: 0;

      :deep(img) {
        height: 30px;
        object-fit: contain;
      }
    }
  }

  .deals-link-mobile {
    background: linear-gradient(180deg, $color-red 0%, $color-red-dark 100%);

    > span {
      color: $color-white;
    }
  }
}

/**
Desktop links
 */
@include all-desktop() {
  .link-wrap {
    background: $color-black-light;
    height: 48px;
  }

  .links {
    justify-content: space-around;
    position: relative;
  }

  .has-dropdown {
    display: block;
    position: relative;
  }

  .hideable-links {
    flex-grow: 1;
    height: 48px;
    flex-wrap: wrap;
    overflow: hidden;
    justify-content: space-around;
  }

  .link:hover, .link.active {
    > span {
      color: $color-red;
    }
  }

  .link > span, .shop-btn {
    color: white;
    text-transform: uppercase;
    font-family: $font-header;
    font-weight: $weight-bold;
    height: 48px;
    line-height: 48px;
  }

  .shop-btn {
    background: linear-gradient(180deg, #EA0000 0%, #BC0000 100%);
    border: none;
    font-size: 1em;
    margin: 0 4px;
  }

  .red-link {
    background: linear-gradient(180deg, #EA0000 0%, #BC0000 100%);

    &:hover > span {
      color: $color-white;
    }
  }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 0;
  }

  .has-icon {
    align-items: center;

    > .icon {
      padding: 0;
    }

    > .rg-img {
      height: 50px;
      width: 28px;
    }

    &.has-dropdown > .icon {
      width: 10px;
      margin-left: -4px;
      margin-right: $space-xs;
    }
  }

  .hover-dropdown {
    margin-right: auto;
    &:hover:not(.force-hide) {
      .dropdown {
        display: flex;
      }
    }
  }

  .mobile-event-links {
    display: none;
  }

  .deals-link-mobile {
    display: none;
  }
}

</style>
